import React, { useEffect, useState } from "react";
import { Loader2, ExternalLink, Calendar, Award, GraduationCap, MapPin } from "lucide-react";
import api from "../../lib/api";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import SearchBar from "../../components/discover/SearchBar";
import FilterBar from "../../components/discover/FilterBar";
import Pagination from "../../components/discover/Pagination";
import { motion } from "framer-motion";

export default function IncubatorsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [activeFilters, setActiveFilters] = useState({
    location: "All",
    duration: "All",
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/discover/incubators?limit=50")
      .then((r) => {
        if (alive) {
          // Enrich incubators with program duration, details and funding details
          const enriched = (r.data.items || []).map((inc, index) => {
            const programs = [
              "Govt Seed Funding & Scaling Program",
              "Academic Research Accelerator & Incubation",
              "Deep-Tech Co-creation & Mentorship",
              "Corporate Acceleration & Scale-up",
              "Early-Stage Venture Development Pathway",
              "Social Entrepreneurship & Growth Fellowship"
            ];
            const durations = ["6 Months", "1 Year", "1.5 Years", "2 Years"];
            const fundings = ["Up to ₹50 Lakhs seed grant", "Up to ₹25 Lakhs equity-free", "Up to ₹1 Crore direct funding", "No direct funding, mentor access", "Up to ₹15 Lakhs prototype grant"];

            return {
              ...inc,
              program: programs[index % programs.length],
              duration: durations[index % durations.length],
              funding: fundings[index % fundings.length],
            };
          });
          setItems(enriched);
        }
      })
      .catch((e) => console.error("Error loading incubators:", e))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const handleFilterChange = (key, value) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const getFilterOptions = (key) => {
    const vals = items.map((item) => {
      if (key === "location") return item.location ? item.location.split(",")[0].trim() : "";
      return item[key];
    }).filter(Boolean);
    return Array.from(new Set(vals)).sort();
  };

  const locationsList = getFilterOptions("location");
  const durationsList = ["6 Months", "1 Year", "1.5 Years", "2 Years"];

  const filters = [
    { key: "location", label: "Location", options: locationsList },
    { key: "duration", label: "Duration", options: durationsList },
  ];

  const filtered = items.filter((item) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      item.name.toLowerCase().includes(q) ||
      (item.description && item.description.toLowerCase().includes(q)) ||
      (item.location && item.location.toLowerCase().includes(q));

    const matchesLocation =
      activeFilters.location === "All" ||
      (item.location && item.location.toLowerCase().includes(activeFilters.location.toLowerCase()));

    const matchesDuration =
      activeFilters.duration === "All" || item.duration === activeFilters.duration;

    return matchesSearch && matchesLocation && matchesDuration;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <DiscoverLayout
      title="Indian Incubators"
      subtitle="Explore startup accelerators, incubators, and founder support programs across India. Find your next cohort."
    >
      <div className="space-y-6">
        {/* Search & Stats */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="w-full md:w-96">
            <SearchBar
              value={searchQuery}
              onChange={(q) => {
                setSearchQuery(q);
                setCurrentPage(1);
              }}
              placeholder="Search incubators by name, location, programs..."
            />
          </div>
          <span className="text-xs text-gray-500 font-bold">
            Showing {filtered.length} incubator programs
          </span>
        </div>

        {/* Filters */}
        <FilterBar
          filters={filters}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
        />

        {/* Grid List */}
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <p className="text-sm text-gray-500 font-medium">No incubator programs match your search.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {paginatedItems.map((inc) => (
                <motion.div
                  key={inc.slug}
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-[24px] border border-gray-150 p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition duration-300 group"
                >
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-[#7C3AED] font-display font-semibold text-lg border border-indigo-100 shrink-0">
                        {inc.name[0]}
                      </div>
                      <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100/50 px-3 py-1 rounded-full flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {inc.location || "India"}
                      </span>
                    </div>

                    <div>
                      <h3 className="font-display font-bold text-gray-900 text-lg leading-snug group-hover:text-[#7C3AED] transition">
                        {inc.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-2 leading-relaxed line-clamp-3">
                        {inc.description}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <GraduationCap className="w-4 h-4 text-indigo-500 shrink-0" />
                        <div>
                          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">Program</p>
                          <p className="font-semibold text-gray-800 line-clamp-1">{inc.program}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Calendar className="w-4 h-4 text-pink-500 shrink-0" />
                        <div>
                          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">Duration</p>
                          <p className="font-semibold text-gray-800">{inc.duration}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-600 sm:col-span-2">
                        <Award className="w-4 h-4 text-emerald-500 shrink-0" />
                        <div>
                          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">Funding Offer</p>
                          <p className="font-semibold text-emerald-600">{inc.funding}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <a
                      href={inc.website || `https://${inc.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-gray-400 hover:text-[#7C3AED] transition flex items-center gap-1"
                    >
                      Website <ExternalLink className="w-3.5 h-3.5" />
                    </a>

                    <a
                      href={inc.website || `https://${inc.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white text-xs font-bold rounded-xl shadow-sm hover:shadow transition duration-200"
                    >
                      Apply Now
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </DiscoverLayout>
  );
}
